import { parseResponse, tokenRequestOptions, tokenRequestOptionsForMultipart } from "../store/actions/LoginActions";
import { BonusPointsDto } from "../store/models/BonusPointsDto";
import { BonusPremiumDto } from "../store/models/BonusPremiumDto";
import { BranchStoreDto } from "../store/models/BranchStoreDto";
import { ConsentDto } from "../store/models/ConsentDto";
import { CouponDto } from "../store/models/CouponDto";
import { CustomerDto } from "../store/models/CustomerDto";
import { CustomerAvatarResponse } from "../store/models/CustomerAvatarDto";
import CustomerPreferenceDto from "../store/models/CustomerPreferenceDto";
import { ConfigTypes, FieldConfigDto } from "../store/models/FieldConfigDto";
import { CustomerHistoryDto } from "../store/models/HistoryDto";
import { InvitationMeDto } from "../store/models/InvitationMeDto";
import { OperationalUnitDto } from "../store/models/OperationalUnitDto";
import PreferenceDto from "../store/models/PreferenceDto";
import { RegistrationDto } from "../store/models/RegistrationDto";
import { ConfigGroupKey } from "../store/models/ConfigGroupKey";
import { fetchWithTenant } from "./ApiUtils";
import { PaginatedReceiptsDto } from "../store/models/ReceiptsDto";
import { PaginatedCouponTemplatesDto, PaginatedIssuedCouponsDto } from "../store/models/CouponTemplateDto";

export const REACT_APP_SERVER_URL = window.env.REACT_APP_SERVER_URL || process.env.REACT_APP_SERVER_URL;

const REACT_APP_BONUS_MANAGEMENT_URL =
  window.env.REACT_APP_BONUS_MANAGEMENT_URL || process.env.REACT_APP_BONUS_MANAGEMENT_URL;
const REACT_APP_CUSTOMER_CARE_URL = window.env.REACT_APP_CUSTOMER_CARE_URL || process.env.REACT_APP_CUSTOMER_CARE_URL;
const REACT_APP_CUSTOMER_INTERACTION_URL =
  window.env.REACT_APP_CUSTOMER_INTERACTION_URL || process.env.REACT_APP_CUSTOMER_INTERACTION_URL;
const REACT_APP_REGISTRATION_URL = window.env.REACT_APP_REGISTRATION_URL || process.env.REACT_APP_REGISTRATION_URL;
const REACT_APP_HISTORY_URL = window.env.REACT_APP_HISTORY_URL || process.env.REACT_APP_HISTORY_URL;
const REACT_APP_RECEIPTS_URL = window.env.REACT_APP_RECEIPTS_URL || process.env.REACT_APP_RECEIPTS_URL;
const REACT_APP_COUPON_URL = window.env.REACT_APP_COUPON_URL || process.env.REACT_APP_COUPON_URL;

const customerCareUrl = REACT_APP_CUSTOMER_CARE_URL || `${REACT_APP_SERVER_URL}/customercare/v1`;
const registrationUrl = REACT_APP_REGISTRATION_URL || `${REACT_APP_SERVER_URL}/auth/v1`;
const customerInteractionUrl = REACT_APP_CUSTOMER_INTERACTION_URL || `${REACT_APP_SERVER_URL}/customerinteraction/v1`;
const bonusManagementUrl = REACT_APP_BONUS_MANAGEMENT_URL || `${REACT_APP_SERVER_URL}/bonusmanagement/v1`;
const receiptsUrl = REACT_APP_RECEIPTS_URL || `${REACT_APP_SERVER_URL}/receipt-scanning/v1`;
const historyUrl = REACT_APP_HISTORY_URL || `${REACT_APP_SERVER_URL}/history/v1`;
const couponUrl = REACT_APP_COUPON_URL || `${REACT_APP_SERVER_URL}/coupons/v1`;

const objectToQueryString = (obj: object) => {
  const entries = Object.entries(obj).filter(([_, value]) => value !== undefined);
  return entries.length ? `?${entries.map(([key, value]) => key + "=" + value).join("&")}` : "";
};

const API = {
  findCustomer: async () => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(`${customerCareUrl}/loyalty/customers/me`, requestOptions).then(parseResponse);
  },
  updateCustomer: async (customer: CustomerDto) => {
    const requestOptions = await tokenRequestOptions("PUT");
    return fetchWithTenant(`${customerCareUrl}/loyalty/customers/me`, {
      ...requestOptions,
      body: JSON.stringify(customer),
    }).then(parseResponse);
  },
  register: async (registration: RegistrationDto, invitation?: string): Promise<CustomerDto> => {
    const requestOptions = await tokenRequestOptions("POST", false);
    return fetchWithTenant(
      `${registrationUrl}/loyalty/register${objectToQueryString({
        invitation,
      })}`,
      {
        ...requestOptions,
        body: JSON.stringify(registration),
      }
    ).then(parseResponse);
  },
  cancelMembership: async (): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST");
    return fetchWithTenant(`${customerCareUrl}/loyalty/customers/me/cancel-membership`, {
      ...requestOptions,
    }).then(parseResponse);
  },
  blockCustomer: async (): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST");
    return fetchWithTenant(`${customerCareUrl}/loyalty/customers/me/block`, {
      ...requestOptions,
    }).then(parseResponse);
  },
  consent: async (consent: ConsentDto): Promise<CustomerDto> => {
    const requestOptions = await tokenRequestOptions("POST", false);
    return fetchWithTenant(`${customerCareUrl}/customers/${consent.externalCustomerId}/consent`, {
      ...requestOptions,
      body: JSON.stringify(consent),
    }).then(parseResponse);
  },
  resendVerificationLink: async (customerNumber: string): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST", false);
    return fetchWithTenant(`${registrationUrl}/register/${customerNumber}`, {
      ...requestOptions,
      body: "{}",
    }).then(parseResponse);
  },
  getCustomerPreferences: async (): Promise<PreferenceDto> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(`${customerCareUrl}/loyalty/customers/me/preferences`, requestOptions).then(parseResponse);
  },
  updateCustomerPreferences: async (customerPreferences: CustomerPreferenceDto[]) => {
    const requestOptions = await tokenRequestOptions("PUT");
    return fetchWithTenant(`${customerCareUrl}/loyalty/customers/me/preferences`, {
      ...requestOptions,
      body: JSON.stringify(customerPreferences),
    }).then(parseResponse);
  },
  getCustomerHistory: async (): Promise<CustomerHistoryDto> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(`${historyUrl}/loyalty/history/me`, requestOptions).then(parseResponse);
  },
  getBonusPointsInfo: async (): Promise<BonusPointsDto> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(`${customerInteractionUrl}/loyalty/customers/me/points`, requestOptions).then(parseResponse);
  },
  findCoupons: async (): Promise<CouponDto[]> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(`${couponUrl}/customers/me/coupons`, requestOptions).then(parseResponse);
  },
  findCoupon: async (couponCode: string): Promise<CouponDto> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(`${couponUrl}/coupons/${couponCode}`, requestOptions).then(parseResponse);
  },
  createCoupon: async (templateNumber: string): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST");
    return fetchWithTenant(`${couponUrl}/customers/me/coupons`, {
      ...requestOptions,
      body: JSON.stringify({
        couponTemplateNumber: templateNumber,
      }),
    }).then(parseResponse);
  },
  findActivePremiums: async (): Promise<BonusPremiumDto> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(`${customerInteractionUrl}/premiums`, requestOptions).then(parseResponse);
  },
  getOperationalRootUnitList: async (): Promise<OperationalUnitDto[]> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(`${bonusManagementUrl}/units/rootlist`, {
      ...requestOptions,
    }).then(parseResponse);
  },
  getBranchStoreList: async (): Promise<BranchStoreDto[]> => {
    const requestOptions = await tokenRequestOptions("GET", false);
    return fetchWithTenant(`${customerInteractionUrl}/units/store`, {
      ...requestOptions,
    }).then(parseResponse);
  },
  invite: async (invitation: InvitationMeDto): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST");
    return fetchWithTenant(`${registrationUrl}/loyalty/invitations/me`, {
      ...requestOptions,
      body: JSON.stringify(invitation),
    }).then(parseResponse);
  },
  getRegistrationFieldConfig: async (type: ConfigTypes): Promise<FieldConfigDto[]> => {
    const requestOptions = await tokenRequestOptions("GET", false);
    return fetchWithTenant(
      `${registrationUrl}/configs/registration/fields${objectToQueryString({
        type,
      })}`,
      {
        ...requestOptions,
      }
    ).then(parseResponse);
  },
  getRegistrationConfig: async (configGroups: ConfigGroupKey[]): Promise<FieldConfigDto[]> => {
    const requestOptions = await tokenRequestOptions("GET", false);
    return fetchWithTenant(
      `${registrationUrl}/configs${objectToQueryString({
        configGroups,
      })}`,
      {
        ...requestOptions,
      }
    ).then(parseResponse);
  },
  getCustomerConfig: async (configGroups: ConfigGroupKey[]): Promise<FieldConfigDto[]> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(
      `${customerCareUrl}/configs${objectToQueryString({
        configGroups,
      })}`,
      {
        ...requestOptions,
      }
    ).then(parseResponse);
  },
  getCustomerFieldConfig: async (type: ConfigTypes): Promise<FieldConfigDto[]> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(
      `${customerCareUrl}/configs/customer/fields${objectToQueryString({
        type,
      })}`,
      {
        ...requestOptions,
      }
    ).then(parseResponse);
  },
  getReceipts: async (page: number, size: number): Promise<PaginatedReceiptsDto> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(
      `${receiptsUrl}/receipts/me${objectToQueryString({
        page,
        size,
      })}`,
      {
        ...requestOptions,
      }
    ).then(parseResponse);
  },
  uploadReceipts: async (formData: FormData): Promise<void> => {
    const requestOptions = await tokenRequestOptionsForMultipart("POST");
    return fetchWithTenant(`${receiptsUrl}/receipts/upload`, {
      ...requestOptions,
      body: formData,
    }).then(parseResponse);
  },

  // Coupon Template APIs
  getCouponTemplates: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
    status?: string[];
  }): Promise<PaginatedCouponTemplatesDto> => {
    const requestOptions = await tokenRequestOptions("GET");
    const queryParams = params ? objectToQueryString(params) : "";
    return fetchWithTenant(`${couponUrl}/customers/me/coupon-templates${queryParams}`, requestOptions).then(
      parseResponse
    );
  },

  // Customer Coupon APIs
  getMyCoupons: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
    status?: string[];
  }): Promise<PaginatedIssuedCouponsDto> => {
    const requestOptions = await tokenRequestOptions("GET");
    const queryParams = params ? objectToQueryString(params) : "";
    return fetchWithTenant(`${couponUrl}/customers/me/coupons${queryParams}`, requestOptions).then(parseResponse);
  },

  issueCouponToMe: async (couponTemplateNumber: string): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST");
    return fetchWithTenant(`${couponUrl}/customers/me/coupons`, {
      ...requestOptions,
      body: JSON.stringify({ couponTemplateNumber }),
    }).then(parseResponse);
  },

  purchaseIssuedCoupon: async (couponNumber: string): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST");
    return fetchWithTenant(`${couponUrl}/customers/me/coupons/${couponNumber}/purchase`, {
      ...requestOptions,
    }).then(parseResponse);
  },

  activateMyCoupon: async (couponNumber: string): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST");
    return fetchWithTenant(`${couponUrl}/customers/me/coupons/${couponNumber}/activate`, {
      ...requestOptions,
    }).then(parseResponse);
  },

  deactivateMyCoupon: async (couponNumber: string): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST");
    return fetchWithTenant(`${couponUrl}/customers/me/coupons/${couponNumber}/deactivate`, {
      ...requestOptions,
    }).then(parseResponse);
  },

  redeemMyCoupon: async (couponNumber: string, code: string, externalReceiptId: string): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST");
    return fetchWithTenant(`${couponUrl}/customers/me/coupons/${couponNumber}/redeem`, {
      ...requestOptions,
      body: JSON.stringify({ code, externalReceiptId }),
    }).then(parseResponse);
  },

  getActivatedCoupons: async (): Promise<PaginatedIssuedCouponsDto> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(`${couponUrl}/customers/me/coupons?status=ACTIVATED`, requestOptions).then(parseResponse);
  },

  redeemCoupon: async (couponNumber: string, code: string, externalReceiptId: string): Promise<void> => {
    const requestOptions = await tokenRequestOptions("POST");
    return fetchWithTenant(`${couponUrl}/customers/me/coupons/${couponNumber}/redeem`, {
      ...requestOptions,
      body: JSON.stringify({
        code,
        externalReceiptId,
      }),
    }).then(parseResponse);
  },

  getCustomerAvatar: async (): Promise<string> => {
    const requestOptions = await tokenRequestOptions("GET");
    return fetchWithTenant(`${customerCareUrl}/loyalty/customers/me/avatar`, requestOptions)
      .then((response) => {
        if (response.status === 404) {
          throw new Error("Avatar not found");
        }
        return parseResponse(response);
      })
      .then((data: CustomerAvatarResponse) => data.imageLink);
  },

  uploadCustomerAvatar: async (file: File): Promise<void> => {
    const requestOptions = await tokenRequestOptionsForMultipart("POST");
    if (!requestOptions) return;

    const formData = new FormData();
    formData.append("image", file);

    return fetchWithTenant(`${customerCareUrl}/loyalty/customers/me/avatar`, {
      ...requestOptions,
      body: formData,
    }).then((response) => {
      if (response.status === 204) {
        return;
      }
      return parseResponse(response);
    });
  },
};

export default API;
